import { useState } from 'react';
import { format } from 'date-fns';
import {
  User,
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  Download,
  Play,
  Pause,
  FileText,
  MapPin,
  Image as ImageIcon,
  Volume2
} from 'lucide-react';
import { ChatMessage, MessageDeliveryStatus } from '@/services/chat.service';
import { cn } from '@caruso/ui';

interface MessageBubbleProps {
  message: ChatMessage;
  showAvatar?: boolean;
  showSenderName?: boolean;
}

const statusIcons: Record<MessageDeliveryStatus, any> = {
  PENDING: Clock,
  SENT: Check,
  DELIVERED: CheckCheck,
  READ: CheckCheck,
  FAILED: AlertCircle,
};

export function MessageBubble({ message, showAvatar = true, showSenderName = true }: MessageBubbleProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null);

  const isOutbound = message.direction === 'OUTBOUND';
  const StatusIcon = statusIcons[message.status];
  const time = format(new Date(message.createdAt), 'HH:mm');
  const senderName = message.senderUser?.name;

  const handlePlayAudio = () => {
    if (!audioRef) {
      const audio = new Audio(message.mediaUrl);
      audio.onended = () => setIsPlaying(false);
      audio.onerror = () => setIsPlaying(false);
      setAudioRef(audio);
      audio.play();
      setIsPlaying(true);
    } else if (isPlaying) {
      audioRef.pause();
      setIsPlaying(false);
    } else {
      audioRef.play();
      setIsPlaying(true);
    }
  };

  const renderContent = () => {
    switch (message.messageType) {
      case 'IMAGE':
        return (
          <div className="max-w-xs">
            {!imageError && message.mediaUrl ? (
              <a href={message.mediaUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={message.mediaUrl}
                  alt="Imagem"
                  className="rounded-lg max-w-full cursor-pointer hover:opacity-90 transition-opacity"
                  onError={() => setImageError(true)}
                />
              </a>
            ) : (
              <div className="flex items-center gap-2 p-3 bg-brand-smoke/30 rounded-lg">
                <ImageIcon className="w-5 h-5 text-brand-ivory/50" />
                <span className="text-sm text-brand-ivory/50">Imagem nao disponivel</span>
              </div>
            )}
            {message.content && (
              <p className="mt-1 text-sm">{message.content}</p>
            )}
          </div>
        );

      case 'VIDEO':
        return (
          <div className="max-w-xs">
            {message.mediaUrl ? (
              <video
                src={message.mediaUrl}
                controls
                className="rounded-lg max-w-full"
              />
            ) : (
              <div className="flex items-center gap-2 p-3 bg-brand-smoke/30 rounded-lg">
                <Play className="w-5 h-5 text-brand-ivory/50" />
                <span className="text-sm text-brand-ivory/50">Video nao disponivel</span>
              </div>
            )}
            {message.content && (
              <p className="mt-1 text-sm">{message.content}</p>
            )}
          </div>
        );

      case 'AUDIO':
        return (
          <div className="flex items-center gap-3 min-w-[200px]">
            <button
              onClick={handlePlayAudio}
              className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center hover:bg-brand-gold/30 transition-colors"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 text-brand-gold" />
              ) : (
                <Play className="w-5 h-5 text-brand-gold ml-0.5" />
              )}
            </button>
            <div className="flex-1">
              <div className="h-1 bg-brand-gold/20 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-gold transition-all"
                  style={{ width: isPlaying ? '100%' : '0%' }}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                <Volume2 className="w-3 h-3 text-brand-ivory/40" />
                <span className="text-xs text-brand-ivory/40">Audio</span>
              </div>
            </div>
          </div>
        );

      case 'DOCUMENT':
        return (
          <a
            href={message.mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 bg-brand-smoke/30 rounded-lg hover:bg-brand-smoke/50 transition-colors"
          >
            <div className="w-10 h-10 rounded-lg bg-brand-gold/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-brand-gold" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">
                {message.mediaFileName || 'Documento'}
              </p>
              <p className="text-xs text-brand-ivory/50">Clique para baixar</p>
            </div>
            <Download className="w-4 h-4 text-brand-ivory/50" />
          </a>
        );

      case 'LOCATION':
        const locationData = message.metadata as any;
        return (
          <div className="flex items-center gap-3 p-3 bg-brand-smoke/30 rounded-lg">
            <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-red-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">Localizacao</p>
              {locationData?.address && (
                <p className="text-xs text-brand-ivory/50 truncate">
                  {locationData.address}
                </p>
              )}
            </div>
          </div>
        );

      case 'STICKER':
        return message.mediaUrl ? (
          <img
            src={message.mediaUrl}
            alt="Sticker"
            className="w-32 h-32 object-contain"
          />
        ) : (
          <span className="text-brand-ivory/50">Sticker</span>
        );

      case 'SYSTEM':
        return (
          <div className="text-center">
            <span className="text-xs text-brand-ivory/50 italic">
              {message.content}
            </span>
          </div>
        );

      default:
        return (
          <p className="text-sm whitespace-pre-wrap break-words">
            {message.content}
          </p>
        );
    }
  };

  // System messages have special styling
  if (message.messageType === 'SYSTEM') {
    return (
      <div className="flex justify-center py-1">
        <span className="px-3 py-1 bg-brand-smoke/50 rounded-full text-xs text-brand-ivory/50 italic">
          {message.content}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex gap-2',
        isOutbound ? 'justify-end' : 'justify-start'
      )}
    >
      {/* Avatar (inbound only) */}
      {!isOutbound && showAvatar && (
        <div className="w-8 h-8 rounded-full bg-brand-gold/20 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-brand-gold" />
        </div>
      )}
      {!isOutbound && !showAvatar && <div className="w-8" />}

      {/* Message Bubble */}
      <div
        className={cn(
          'max-w-[70%] rounded-2xl px-3 py-2',
          isOutbound
            ? 'bg-brand-gold/20 text-brand-ivory rounded-tr-sm'
            : 'bg-brand-smoke/80 text-brand-ivory rounded-tl-sm'
        )}
      >
        {/* Nome do remetente (apenas para mensagens de saida) */}
        {isOutbound && showSenderName && senderName && (
          <p className="text-xs text-brand-gold font-medium mb-1">
            {senderName}
          </p>
        )}
        {renderContent()}

        {/* Time and Status */}
        <div className={cn(
          'flex items-center gap-1 mt-1',
          isOutbound ? 'justify-end' : 'justify-start'
        )}>
          <span className="text-xs text-brand-ivory/40">{time}</span>
          {isOutbound && StatusIcon && (
            <StatusIcon
              className={cn(
                'w-3.5 h-3.5',
                message.status === 'READ' ? 'text-blue-400' :
                message.status === 'FAILED' ? 'text-red-400' :
                'text-brand-ivory/40'
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
}
